/**
 * datevExport — Herzstück des DATEV-Buchungsstapel-Exports (M7, EXTF-CSV).
 *
 * Reine, deterministische String-/Zahlen-Transformation OHNE Seiteneffekt
 * (KEINE Firebase-/React-Imports) — voll unit-testbar gegen Soll-CSV-Fixtures.
 *
 * Leitprinzipien (architecture-m7.md + solar-m7-datev-ruling.md):
 *   - Umsatz STETS abs() + BRUTTO auf Automatik-/Sachkonto; Richtung allein über S/H.
 *   - Gutschrift/Storno (documentTypeCode 381 / negative Cents / isCancellation) wird
 *     NICHT herausgefiltert, sondern mit umgekehrtem S/H als §17-Korrektur gebucht.
 *   - Integer-Cent-Disziplin durchgängig; einmalige Komma-Formatierung am CSV-Rand.
 *   - A9: Brutto-Summe an `totalAmount` (Beleg-Brutto) ankern; Cent-Differenz ≤1 auf den
 *     betragsgrößten Satz; >1 Cent → fail-closed Bucket (`amountMismatch`).
 *   - Alles Unsichere → sichtbarer `excluded`-Bucket, NIE still falsch gebucht.
 *   - `buildDatevExport` ist TOTAL (wirft nie) — partielle/leere Daten → Bucket statt Fehler.
 *
 * @module datevExport
 */

import type { TaxScheme } from '../types/expenseTypes';
import {
  SkrKontenrahmen,
  mapRevenueAccount,
  mapExpenseAccount,
  mapReisekostenAccount,
  DATEV_COLLECTIVE_ACCOUNTS,
  DATEV_PRIVATEINLAGE_ACCOUNTS,
} from './datevMapping';
// M7b: GUID-Ableitung für documentRefs + Beleglink-Injektion (M7b-5a). datevBelege
// importiert von hier NUR Typen (`import type`, zur Laufzeit gelöscht) → kein
// Laufzeit-Zyklus trotz gegenseitiger Referenz.
import { deriveBelegGuid, assignBeleglinks } from './datevBelege';

// ─── Eingabe-/Ausgabe-Typen ─────────────────────────────────────────────────────

/** Ein einzelner Buchungssatz (vor CSV-Serialisierung). */
export interface DatevBookingRow {
  /** Umsatz IMMER > 0 (abs, Brutto). Richtung trägt `sollHaben`. */
  umsatzCents: number;
  /** Soll/Haben-Kennzeichen bezogen auf das Sachkonto (`konto`). */
  sollHaben: 'S' | 'H';
  /** Sachkonto (Erlös-/Aufwandskonto). */
  konto: string;
  /** Gegenkonto (Personen-/Sammelkonto). */
  gegenkonto: string;
  /**
   * BU-/Steuerschlüssel oder null (Automatikkonto ODER Ausschluss-Bucket).
   * Herkunft: `mapRevenueAccount`/`mapExpenseAccount` in `datevMapping.ts`, die
   * gesamte fachliche Falltabelle + bewusste Bucket-Entscheidungen sind dort im
   * Modul-Kopfkommentar unter „WL-K4-Traceability" dokumentiert (modules-loop-b.md).
   */
  buKey: string | null;
  /** Belegdatum als "TTMM" (4-stellig, Jahr aus Kopfzeile). */
  belegdatumTTMM: string;
  /** Belegfeld 1 (Rechnungs-/Belegnummer). */
  belegfeld1: string;
  /** Buchungstext (CP1252-safe, ≤ 60 Zeichen). */
  buchungstext: string;
  /**
   * Stabiler Dokument-Schlüssel `${collection}:${docId}` — verbindet Row↔Beleg (M7b).
   * Nur gesetzt, wenn die Quelle eine `docId` trägt; sonst weggelassen (Altpfad). Optional.
   */
  documentKey?: string;
  /**
   * DATEV-Beleglink-Zellwert für Spalte 20 (Index 19), z. B. `BEDI "<GUID>"` (M7b).
   * Wird über `assignBeleglinks` NUR gesetzt, wenn tatsächlich eine Datei vorliegt
   * (fail-closed — nie ein toter Link). Leer/undefined → Spalte 20 bleibt leer. Optional.
   */
  beleglink?: string;
}

/**
 * Verweis-Deskriptor je gebuchtem Beleg (M7b) — verbindet einen Buchungsbeleg mit
 * seiner Datei im Belege-ZIP. Ein Beleg mit mehreren Steuersätzen erzeugt mehrere
 * `DatevBookingRow`s, aber genau EINEN `DatevDocumentRef` (eine Datei).
 *
 * Der Client sendet diesen Deskriptor an die ZIP-Function; die Function vertraut ihm
 * NICHT (recomputet GUID + liest autoritative Pfade selbst).
 */
export interface DatevDocumentRef {
  /** `${collection}:${docId}` — identisch zum `documentKey` der zugehörigen Rows. */
  documentKey: string;
  collection: 'invoices' | 'expenses';
  docId: string;
  /** Erwarteter Datei-Typ: ZUGFeRD-PDF, XRechnung-XML oder Ausgaben-Scan. */
  fileKind: 'pdf' | 'xml' | 'receipt';
  /**
   * true, wenn auf Input-Ebene eine Datei erwartet wird. Für Invoices genau dann, wenn
   * eine ZUGFeRD-PDF vorliegt (`DatevInvoiceInput.hasPdf`, M7b-5d/P5 — reine XRechnung-XML
   * zählt nicht); für Expenses genau dann, wenn `receiptStorageRef` gesetzt+nicht-leer ist.
   * Der Server korrigiert real fehlende Dateien.
   */
  hasFileExpectation: boolean;
  /** Deterministischer GUID (UUIDv5, Uppercase) aus `documentKey` — s. `deriveBelegGuid`. */
  guid: string;
  /** Vorgeschlagener bloßer Dateiname im ZIP-Root, z. B. `Re2026-001.pdf` (M7b-5c). Server ist Autorität. */
  filenameInZip: string;
}

/** Stabile Ausschluss-Gründe (= i18n-Key-Fragmente `datev.reason.<reason>`). */
export type DatevExclusionReason =
  | 'taxSchemeNeedsConfirm'
  | 'amountMismatch'
  | 'nonEur'
  | 'missingFields'
  | 'zeroAmount'
  | 'zeroRateAmbiguous'
  | 'draft'
  // RK-6 (additiv): stornierte Reise — im MVP kein Umkehr-Pfad, nur sichtbarer Ausschluss
  // (§3.5/§6.3). `draft`/`zeroAmount` werden für Reisen wiederverwendet.
  | 'travelCancelled';

/** Ein vom Export ausgeschlossener Beleg (§4.6 Bucket). */
export interface DatevExcludedItem {
  source: 'invoice' | 'expense' | 'travel';
  belegnr: string | null;
  dateISO: string;
  grossCents: number;
  reason: DatevExclusionReason;
}

/** Header-Stammdaten (aus Seller-Settings + Zeitraum). */
export interface DatevHeaderConfig {
  beraterNr: string | null;
  mandantenNr: string | null;
  skr: SkrKontenrahmen;
  sachkontenLaenge: number;
  /** ISO YYYY-MM-DD, inklusive untere Periodengrenze. */
  fromISO: string;
  /** ISO YYYY-MM-DD, inklusive obere Periodengrenze. */
  toISO: string;
  /** ISO-Zeitstempel der Erzeugung (für „Erzeugt am" 17-stellig). */
  generatedAtISO: string;
}

/** Pro Steuersatz rekonstruierte Bemessungsgrundlage (Page-Mapper, §3.1/§4.7). */
export interface DatevPerRate {
  taxRate: number;
  netCents: number;
  vatCents: number;
  /** EN-16931 BT-151, falls API-/Altpfad — Rückfall für die eindeutigen Codes. */
  taxCategory: string | null;
  /**
   * Präziser VB-1-Auswahl-Schlüssel (`taxCategorySelection`), falls die Rechnung ihn
   * trägt. Hat Vorrang vor `taxCategory` und disambiguiert `E` (§19 vs. §4).
   * Optional (Altbelege/`taxSummary`-Pfad ohne Position → undefined).
   */
  taxCategorySelection?: string | null;
}

/** Eingabe-Ausgangsrechnung, entkoppelt von der Firestore-Doc-Form. */
export interface DatevInvoiceInput {
  invoiceNumber: string | null;
  dateISO: string;
  buyerName: string;
  isCancellation: boolean;
  /** §19-Seller-Status (einziger 0 %-Erlös-Trigger). */
  isSmallBusiness: boolean;
  perRate: DatevPerRate[];
  /** Σ perRate Brutto (Kontrollwert; intern neu summiert). */
  reconstructedGrossCents: number;
  /** Beleg-Brutto (totalAmount → Cents) — Anker für A9. */
  totalAmountCents: number;
  currency: string;
  /**
   * Liegt die Käufer-USt-IdNr. (BT-48) auf der Rechnung vor? Steuert das IGL-Gate
   * (Auflage 2): `intra_community_6a` wird nur ohne diese Angabe gebucketet.
   * Optional (Altbelege → undefined → fail-closed kein IGL-Mapping).
   */
  buyerVatIdPresent?: boolean;
  /**
   * Firestore-Doc-ID der Rechnung. Trägt die Beleglink-/Datei-Auflösung (M7b).
   * Optional (Altpfad ohne Beleg-Verknüpfung).
   */
  docId?: string;
  /**
   * true, wenn eine ZUGFeRD-PDF vorliegt (aus `InvoiceDoc.pdfPath`). Steuert
   * `hasFileExpectation` + Beleglink (M7b-5d / P5): reine XRechnung-XML ohne PDF ist
   * kein lesbares Belegbild → fehlt/false ⇒ Buchung „ohne Beleg" (kein Beleglink,
   * kein Ref an die Function). Optional (Altpfad → undefined → fail-closed kein Beleg).
   */
  hasPdf?: boolean;
}

/** Eingabe-Ausgabe, entkoppelt von der Firestore-Doc-Form. */
export interface DatevExpenseInput {
  invoiceNumber: string | null;
  dateISO: string;
  supplierName: string;
  /** Defensiv `?? null` an der Call-Site (Feld optional in `Expense`). */
  documentTypeCode: string | null;
  netCents: number;
  vatOwedCents: number;
  vatDeductibleCents: number;
  grossCents: number;
  vatRate: number | null;
  taxScheme: TaxScheme;
  vatDeductible: boolean;
  skrAccount: string | null;
  status: 'draft' | 'booked';
  completenessStatus: 'complete' | 'incomplete' | 'needs_review';
  /** Fremdwährungs-Markierung (≠EUR → Bucket). */
  nonEurCurrency: string | null;
  /**
   * Firestore-Doc-ID der Ausgabe. Trägt die Beleglink-/Datei-Auflösung (M7b). Optional.
   */
  docId?: string;
  /**
   * Storage-Ref des Beleg-Scans (aus `Expense.receiptStorageRef`), falls vorhanden.
   * Steuert `hasFileExpectation` (M7b). Optional (`null` = kein Beleg-Scan).
   */
  receiptStorageRef?: string | null;
  /**
   * Expense-Storno (Variante B, Privatentnahme): explizites Gegenkonto-Override
   * (aus `Expense.privatentnahmeAccount`), das statt des Kreditor-Sammelkontos
   * (`DATEV_COLLECTIVE_ACCOUNTS.creditor`) gesetzt wird. Additiv optional —
   * `undefined`/`null`/leer ⇒ verhaltensneutral (bestehendes Sammelkonto, wie
   * bisher). Nur `processExpense` liest dieses Feld; `processInvoice` unberührt.
   */
  gegenkontoOverride?: string | null;
}

/**
 * Beleglose Pauschal-Buchung einer ABGERECHNETEN Reise (RK-6, §3.5). Entkoppelt von der
 * `travel_trips`-Doc-Form — erzeugt aus `mapSettledTripToDatevTravelInput`
 * (`utils/travel/travelDatevMapper.ts`). Ausschließlich beleglose Pauschalen (Verpflegung +
 * Kilometergeld); belegbehaftete Ist-Kosten laufen unverändert über den `expenses`-Weg.
 */
export interface DatevTravelInput {
  /** Firestore-Doc-ID der Reise (Diagnose/Trace). */
  tripId: string;
  /**
   * Belegfeld 1: `'RK-' + YYYYMMDD(returnDateISO) + '-' + docId.slice(0,6)` (Case unverändert),
   * EXAKT 18 Zeichen (E5, DATEV-Limit 36 ok). In BEIDEN erzeugten Rows identisch.
   */
  belegLabel: string;
  /**
   * Buchungstext-Basis, z. B. `'Reise Berlin 12.03.-14.03.'`. Der Export hängt
   * LEERZEICHEN-getrennt `'Verpflegung'` (Row 1) bzw. `'Fahrtkosten'` (Row 2) an;
   * CP1252-Sanitizing + Längen-Truncation erfolgen danach im bestehenden Export-Pfad.
   */
  buchungstextBase: string;
  /** Belegdatum (→ TTMM) UND Perioden-Filter-Anker (Rückkehrtag). 'YYYY-MM-DD'. */
  returnDateISO: string;
  /** Verpflegungspauschale in Integer-Cents (≥ 0, aus `settlement.calc`). */
  verpflegungCents: number;
  /** Kilometergeld in Integer-Cents (≥ 0, aus `settlement.calc`). */
  mileageCents: number;
  /** Lebenszyklus-Status der Reise (steuert Bucket vs. Buchung, §3.5). */
  status: 'draft' | 'settled' | 'cancelled';
}

/** Reines Gesamt-Ergebnis. */
export interface DatevExportResult {
  rows: DatevBookingRow[];
  excluded: DatevExcludedItem[];
  /** Header + Spaltenzeile + Datenzeilen (noch NICHT CP1252-kodiert). */
  csvLines: string[];
  bookedCount: number;
  excludedCount: number;
  /**
   * Ein `DatevDocumentRef` je gebuchtem Beleg mit `docId` (M7b) — NICHT je CSV-Zeile.
   * Ein Beleg mit mehreren Steuersätzen (mehrere Rows) liefert genau einen Ref.
   */
  documentRefs: DatevDocumentRef[];
}

/** Optionen — Defaults fail-closed. */
export interface DatevExportOptions {
  /** Default true: nur status==='booked' wird gebucht; Entwürfe → Bucket `draft`. */
  bookedExpensesOnly?: boolean;
}

// ─── EXTF-Format-Konstanten (e-rechnung-Gate, A1 referenz-abgesichert) ──────────

/** EXTF-Kennung (fix). */
const EXTF_KENNUNG = 'EXTF';
/** Versionsnummer (fix, e-rechnung-Gate bestätigt). */
const EXTF_VERSION = '700';
/** Datenkategorie 21 = Buchungsstapel (fix). */
const EXTF_KATEGORIE = '21';
/** Formatname (fix). */
const EXTF_FORMATNAME = 'Buchungsstapel';
/** Formatversion 13 (aktuell, e-rechnung-Empfehlung). */
const EXTF_FORMATVERSION = '13';
/** WKZ (nur EUR im MVP). */
const WKZ_EUR = 'EUR';
/**
 * Festschreibung = 0 (NICHT festgeschrieben) — zwingend (jurist-Auflage 2, GoBD):
 * der Steuerberater schreibt im DATEV-Bestand fest; `1` aus der App wäre fachlich falsch.
 */
const FESTSCHREIBUNG = '0';

/**
 * Spaltenanzahl der Datenzeilen (Formatversion 13).
 *
 * A1 (e-rechnung-Gate): gegen die echte EXTF-Formatversion-13-Referenzdatei
 * abgesichert — `datevExtfReconcile.test.ts` reconciled `DATA_COLUMN_NAMES`
 * (unten) field-by-field gegen `__fixtures__/EXTF_Buchungsstapel.reference.csv`
 * (die offizielle DATEV-Musterdatei). Dies ist der EINE Stellpunkt (SoT).
 */
export const DATEV_DATA_COLUMN_COUNT = 125;

/** Kopfzeilen-Feldanzahl (Formatversion 13) — referenz-abgesichert wie {@link DATEV_DATA_COLUMN_COUNT}. */
const DATEV_HEADER_FIELD_COUNT = 31;

/**
 * Alle 125 offiziellen DATEV-EXTF-Spaltennamen (Formatversion 13), 0-basiert.
 *
 * Abgeleitet aus der autoritativen DATEV-Referenzdatei
 * `__fixtures__/EXTF_Buchungsstapel.reference.csv` (UTF-8, Zeile 2).
 * Trennzeichen ist der ASCII-Hyphen (U+002D, „-"), NICHT der EN-Dash — 1:1 aus der
 * Referenz übernommen (inkl. des unregelmäßigen Spacings bei „Zusatzinformation- Inhalt N").
 * Reihenfolge und Schreibweise sind normativ — bitte keine Eigenmächtigkeit.
 */
const DATA_COLUMN_NAMES: readonly string[] = [
  'Umsatz (ohne Soll/Haben-Kz)',               // 0
  'Soll/Haben-Kennzeichen',                    // 1
  'WKZ Umsatz',                                // 2
  'Kurs',                                      // 3
  'Basis-Umsatz',                              // 4
  'WKZ Basis-Umsatz',                          // 5
  'Konto',                                     // 6
  'Gegenkonto (ohne BU-Schlüssel)',            // 7
  'BU-Schlüssel',                              // 8
  'Belegdatum',                                // 9
  'Belegfeld 1',                               // 10
  'Belegfeld 2',                               // 11
  'Skonto',                                    // 12
  'Buchungstext',                              // 13
  'Postensperre',                              // 14
  'Diverse Adressnummer',                      // 15
  'Geschäftspartnerbank',                      // 16
  'Sachverhalt',                               // 17
  'Zinssperre',                                // 18
  'Beleglink',                                 // 19
  'Beleginfo - Art 1',                         // 20
  'Beleginfo - Inhalt 1',                      // 21
  'Beleginfo - Art 2',                         // 22
  'Beleginfo - Inhalt 2',                      // 23
  'Beleginfo - Art 3',                         // 24
  'Beleginfo - Inhalt 3',                      // 25
  'Beleginfo - Art 4',                         // 26
  'Beleginfo - Inhalt 4',                      // 27
  'Beleginfo - Art 5',                         // 28
  'Beleginfo - Inhalt 5',                      // 29
  'Beleginfo - Art 6',                         // 30
  'Beleginfo - Inhalt 6',                      // 31
  'Beleginfo - Art 7',                         // 32
  'Beleginfo - Inhalt 7',                      // 33
  'Beleginfo - Art 8',                         // 34
  'Beleginfo - Inhalt 8',                      // 35
  'KOST1 - Kostenstelle',                      // 36
  'KOST2 - Kostenstelle',                      // 37
  'Kost-Menge',                                // 38
  'EU-Land u. UStID (Bestimmung)',             // 39
  'EU-Steuersatz (Bestimmung)',                // 40
  'Abw. Versteuerungsart',                     // 41
  'Sachverhalt L+L',                           // 42
  'Funktionsergänzung L+L',                    // 43
  'BU 49 Hauptfunktionstyp',                   // 44
  'BU 49 Hauptfunktionsnummer',                // 45
  'BU 49 Funktionsergänzung',                  // 46
  'Zusatzinformation - Art 1',                 // 47
  'Zusatzinformation- Inhalt 1',               // 48
  'Zusatzinformation - Art 2',                 // 49
  'Zusatzinformation- Inhalt 2',               // 50
  'Zusatzinformation - Art 3',                 // 51
  'Zusatzinformation- Inhalt 3',               // 52
  'Zusatzinformation - Art 4',                 // 53
  'Zusatzinformation- Inhalt 4',               // 54
  'Zusatzinformation - Art 5',                 // 55
  'Zusatzinformation- Inhalt 5',               // 56
  'Zusatzinformation - Art 6',                 // 57
  'Zusatzinformation- Inhalt 6',               // 58
  'Zusatzinformation - Art 7',                 // 59
  'Zusatzinformation- Inhalt 7',               // 60
  'Zusatzinformation - Art 8',                 // 61
  'Zusatzinformation- Inhalt 8',               // 62
  'Zusatzinformation - Art 9',                 // 63
  'Zusatzinformation- Inhalt 9',               // 64
  'Zusatzinformation - Art 10',                // 65
  'Zusatzinformation- Inhalt 10',              // 66
  'Zusatzinformation - Art 11',                // 67
  'Zusatzinformation- Inhalt 11',              // 68
  'Zusatzinformation - Art 12',                // 69
  'Zusatzinformation- Inhalt 12',              // 70
  'Zusatzinformation - Art 13',                // 71
  'Zusatzinformation- Inhalt 13',              // 72
  'Zusatzinformation - Art 14',                // 73
  'Zusatzinformation- Inhalt 14',              // 74
  'Zusatzinformation - Art 15',                // 75
  'Zusatzinformation- Inhalt 15',              // 76
  'Zusatzinformation - Art 16',                // 77
  'Zusatzinformation- Inhalt 16',              // 78
  'Zusatzinformation - Art 17',                // 79
  'Zusatzinformation- Inhalt 17',              // 80
  'Zusatzinformation - Art 18',                // 81
  'Zusatzinformation- Inhalt 18',              // 82
  'Zusatzinformation - Art 19',                // 83
  'Zusatzinformation- Inhalt 19',              // 84
  'Zusatzinformation - Art 20',                // 85
  'Zusatzinformation- Inhalt 20',              // 86
  'Stück',                                     // 87
  'Gewicht',                                   // 88
  'Zahlweise',                                 // 89
  'Forderungsart',                             // 90
  'Veranlagungsjahr',                          // 91
  'Zugeordnete Fälligkeit',                    // 92
  'Skontotyp',                                 // 93
  'Auftragsnummer',                            // 94
  'Buchungstyp',                               // 95
  'USt-Schlüssel (Anzahlungen)',               // 96
  'EU-Land (Anzahlungen)',                     // 97
  'Sachverhalt L+L (Anzahlungen)',             // 98
  'EU-Steuersatz (Anzahlungen)',               // 99
  'Erlöskonto (Anzahlungen)',                  // 100
  'Herkunft-Kz',                               // 101
  'Buchungs GUID',                             // 102
  'KOST-Datum',                                // 103
  'SEPA-Mandatsreferenz',                      // 104
  'Skontosperre',                              // 105
  'Gesellschaftername',                        // 106
  'Beteiligtennummer',                         // 107
  'Identifikationsnummer',                     // 108
  'Zeichnernummer',                            // 109
  'Postensperre bis',                          // 110
  'Bezeichnung SoBil-Sachverhalt',             // 111
  'Kennzeichen SoBil-Buchung',                 // 112
  'Festschreibung',                            // 113
  'Leistungsdatum',                            // 114
  'Datum Zuord. Steuerperiode',                // 115
  'Fälligkeit',                                // 116
  'Generalumkehr (GU)',                        // 117
  'Steuersatz',                                // 118
  'Land',                                      // 119
  'Abrechnungsreferenz',                       // 120
  'BVV-Position',                              // 121
  'EU-Land u. UStID (Ursprung)',               // 122
  'EU-Steuersatz (Ursprung)',                  // 123
  'Abw. Skontokonto',                          // 124
];

// ─── Reine Helfer ───────────────────────────────────────────────────────────────

/**
 * Liegt `dateISO` (inklusive) im Zeitraum [fromISO, toISO]?
 * Lexikografischer Vergleich auf den ersten 10 ISO-Zeichen — keine Zeitzonen-Falle.
 */
function inPeriod(dateISO: string, fromISO: string, toISO: string): boolean {
  if (!dateISO) return false;
  const d = dateISO.slice(0, 10);
  return d >= fromISO.slice(0, 10) && d <= toISO.slice(0, 10);
}

/**
 * Soll/Haben-Kennzeichen je Belegrichtung und Umkehr (§4.4).
 *   - Erlös (revenue): normal H, Umkehr (Storno/negativ) → S.
 *   - Aufwand (expense): normal S, Umkehr (Gutschrift/negativ) → H.
 *
 * @param direction 'revenue' (Ausgangsrechnung) | 'expense' (Ausgabe).
 * @param isReversal Storno/Gutschrift/negativer Betrag.
 */
export function deriveSollHaben(direction: 'revenue' | 'expense', isReversal: boolean): 'S' | 'H' {
  if (direction === 'revenue') {
    return isReversal ? 'S' : 'H';
  }
  return isReversal ? 'H' : 'S';
}

/**
 * Wandelt ISO `YYYY-MM-DD` in das DATEV-Belegdatum `TTMM` (Tag+Monat, 4-stellig).
 * Das Jahr steckt in der Kopfzeile (WJ-Beginn/Periode) — daher NICHT in der Zelle.
 *
 * @example isoToBelegdatumTTMM('2026-03-15') // '1503'
 */
export function isoToBelegdatumTTMM(dateISO: string): string {
  const d = (dateISO ?? '').slice(0, 10);
  const mm = d.slice(5, 7);
  const dd = d.slice(8, 10);
  if (dd.length !== 2 || mm.length !== 2) return '';
  return `${dd}${mm}`;
}

/**
 * Formatiert Integer-Cents als DATEV-Umsatz: abs, Komma-Dezimal, keine Tausender.
 * Reine Integer-Arithmetik (keine Float-Formatierung).
 *
 * @example formatDatevAmount(119000) // '1190,00'
 * @example formatDatevAmount(-59500) // '595,00'
 */
export function formatDatevAmount(cents: number): string {
  const a = Math.abs(Math.trunc(cents));
  const euros = Math.floor(a / 100);
  const rem = a % 100;
  return `${euros},${String(rem).padStart(2, '0')}`;
}

/** Erzeugt den 17-stelligen „Erzeugt am"-Zeitstempel YYYYMMDDHHMMSSFFF aus einem ISO-String. */
function generatedAtToExtf(generatedAtISO: string): string {
  const dt = new Date(generatedAtISO);
  if (Number.isNaN(dt.getTime())) {
    // Fail-soft: niemals throw — fester Platzhalter, der das Format wahrt.
    return '00000000000000000';
  }
  const p2 = (n: number): string => String(n).padStart(2, '0');
  const p3 = (n: number): string => String(n).padStart(3, '0');
  return (
    `${dt.getFullYear()}${p2(dt.getMonth() + 1)}${p2(dt.getDate())}` +
    `${p2(dt.getHours())}${p2(dt.getMinutes())}${p2(dt.getSeconds())}${p3(dt.getMilliseconds())}`
  );
}

/** Liefert YYYYMMDD aus ISO `YYYY-MM-DD` (für WJ-Beginn / Datum von / bis). */
function isoToYyyymmdd(dateISO: string): string {
  return (dateISO ?? '').slice(0, 10).replace(/-/g, '');
}

/** WJ-Beginn (01.01. des Jahres der unteren Periodengrenze) als YYYYMMDD. */
function wirtschaftsjahrBeginn(fromISO: string): string {
  const year = (fromISO ?? '').slice(0, 4);
  return year.length === 4 ? `${year}0101` : '';
}

// ─── CSV-Serialisierung ─────────────────────────────────────────────────────────

/** Maskiert ein Textfeld DATEV-konform: CR/LF entfernen, `"` verdoppeln, in Quotes. Leer → ''. */
function quoteText(value: string): string {
  if (value === null || value === undefined || value.length === 0) return '';
  const cleaned = value.replace(/[\r\n]+/g, ' ').replace(/"/g, '""');
  return `"${cleaned}"`;
}

/** Baut die EXTF-Kopfzeile (Zeile 1) — DATEV-Feldreihenfolge, A1 referenz-abgesichert. */
export function buildExtfHeaderLine(header: DatevHeaderConfig): string {
  const fields: string[] = new Array(DATEV_HEADER_FIELD_COUNT).fill('');

  fields[0] = quoteText(EXTF_KENNUNG); // 1 Kennzeichen
  fields[1] = EXTF_VERSION; // 2 Versionsnummer
  fields[2] = EXTF_KATEGORIE; // 3 Datenkategorie
  fields[3] = quoteText(EXTF_FORMATNAME); // 4 Formatname
  fields[4] = EXTF_FORMATVERSION; // 5 Formatversion
  fields[5] = generatedAtToExtf(header.generatedAtISO); // 6 Erzeugt am (17-stellig)
  // 7 Importiert — leer
  fields[7] = quoteText('RE'); // 8 Herkunft (Rechnungseingang/-ausgang)
  // 9 Exportiert von — leer
  // 10 Importiert von — leer
  fields[10] = header.beraterNr ? header.beraterNr : ''; // 11 Berater (leerbar, A7)
  fields[11] = header.mandantenNr ? header.mandantenNr : ''; // 12 Mandant (leerbar, A7)
  fields[12] = wirtschaftsjahrBeginn(header.fromISO); // 13 WJ-Beginn
  fields[13] = String(header.sachkontenLaenge); // 14 Sachkontenlänge (4)
  fields[14] = isoToYyyymmdd(header.fromISO); // 15 Datum von
  fields[15] = isoToYyyymmdd(header.toISO); // 16 Datum bis
  fields[16] = quoteText(
    `Buchungen ${isoToYyyymmdd(header.fromISO)}-${isoToYyyymmdd(header.toISO)} (SKR${header.skr === 'SKR03' ? '03' : '04'})`,
  ); // 17 Bezeichnung
  // 18 Diktatkürzel — leer
  fields[18] = '1'; // 19 Buchungstyp (1 = Finanzbuchführung)
  fields[19] = '0'; // 20 Rechnungslegungszweck
  fields[20] = FESTSCHREIBUNG; // 21 Festschreibung = 0 (zwingend, jurist-Auflage 2)
  fields[21] = quoteText(WKZ_EUR); // 22 WKZ
  // 23–26 reserviert/leer
  fields[26] = quoteText(header.skr === 'SKR03' ? '03' : '04'); // 27 Sachkontenrahmen (A-1, e-rechnung-Auflage)
  // 28–31 reserviert/leer

  return fields.join(';');
}

/** Baut die Spaltenüberschriften-Zeile (Zeile 2). Konstante Spaltenanzahl. */
function buildColumnHeaderLine(): string {
  const cols: string[] = [];
  for (let i = 0; i < DATEV_DATA_COLUMN_COUNT; i += 1) {
    const name = DATA_COLUMN_NAMES[i];
    cols.push(name ? quoteText(name) : '');
  }
  return cols.join(';');
}

/** Serialisiert einen Buchungssatz zu einer CSV-Datenzeile (konstante Spaltenanzahl). */
function buildDataLine(row: DatevBookingRow): string {
  const cols: string[] = new Array(DATEV_DATA_COLUMN_COUNT).fill('');

  cols[0] = formatDatevAmount(row.umsatzCents); // Umsatz (abs, Komma)
  cols[1] = quoteText(row.sollHaben); // S/H — Textfeld, immer gequotet ("S"/"H")
  // WKZ Umsatz: bei EUR LEER (DATEV-Referenz). Nur Fremdwährung würde `quoteText(wkz)`
  // schreiben — Nicht-EUR wird jedoch upstream abgewiesen, daher hier immer leer.
  cols[2] = '';
  cols[6] = row.konto; // Konto (Sachkonto)
  cols[7] = row.gegenkonto; // Gegenkonto (Personen-/Sammelkonto)
  cols[8] = row.buKey ? quoteText(row.buKey) : ''; // BU-Schlüssel — Textfeld, gequotet wenn gesetzt
  cols[9] = row.belegdatumTTMM; // Belegdatum TTMM
  cols[10] = quoteText(row.belegfeld1); // Belegfeld 1
  cols[13] = quoteText(row.buchungstext); // Buchungstext
  // M7b: Beleglink (Spalte 20 / Index 19). quoteText verdoppelt die inneren `"`
  // DATEV-konform (aus `BEDI "<GUID>"` wird `"BEDI ""<GUID>"""`). Leer → Spalte leer.
  cols[19] = row.beleglink ? quoteText(row.beleglink) : '';

  return cols.join(';');
}

// ─── Belegverarbeitung ──────────────────────────────────────────────────────────

/** Kürzt + säubert den Buchungstext (≤ 60 Zeichen; CP1252-Maskierung erfolgt beim Encoding). */
function buchungstext(name: string): string {
  return (name ?? '').slice(0, 60);
}

// ─── M7b: Dokument-Schlüssel + Belege-Dateiname (rein, deterministisch) ──────────

/**
 * Baut den stabilen `documentKey` `${collection}:${docId}` (M7b). EINE Formel für
 * `row.documentKey` UND `DatevDocumentRef.documentKey` → keine Drift. collection ist
 * kleingeschrieben; `docId` ohne Trim (der GUID-Vertrag verlangt den Roh-Schlüssel).
 */
function makeDocumentKey(collection: 'invoices' | 'expenses', docId: string): string {
  return `${collection}:${docId}`;
}

/** Reduziert einen Pfad-Segment-String auf ZIP-sichere Zeichen (`/`, Leerzeichen etc. → `_`). */
function sanitizeFilenameSegment(value: string): string {
  const cleaned = (value ?? '').replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return cleaned.length > 0 ? cleaned : 'beleg';
}

/**
 * Ermittelt die Dateiendung im ZIP je `fileKind`. Für `receipt` wird — falls vorhanden —
 * die Endung des `receiptStorageRef` übernommen (z. B. `.jpg`), sonst defensiv `.pdf`.
 */
function belegFileExtension(
  fileKind: DatevDocumentRef['fileKind'],
  receiptStorageRef: string | null,
): string {
  if (fileKind === 'pdf') return '.pdf';
  if (fileKind === 'xml') return '.xml';
  const match = (receiptStorageRef ?? '').match(/\.([A-Za-z0-9]{1,8})$/);
  return match ? `.${match[1].toLowerCase()}` : '.pdf';
}

/**
 * Vorschlags-Dateiname im ZIP: bloßer `<Belegnummer|docId>.<ext>` im ZIP-ROOT (M7b-5c/F-4 —
 * kein `belege/`-Präfix mehr; muss mit dem `name`-Attribut der server-gebauten `document.xml`
 * übereinstimmen). Der Server ist Autorität und kann umbenennen/normalisieren; die
 * GUID↔Datei-Bindung trägt zusätzlich die `document.xml`. Belegnummer bevorzugt
 * (menschenlesbar), sonst `docId`.
 */
function buildBelegFilename(
  belegnr: string | null,
  docId: string,
  fileKind: DatevDocumentRef['fileKind'],
  receiptStorageRef: string | null,
): string {
  const base = belegnr && belegnr.trim().length > 0 ? belegnr : docId;
  return `${sanitizeFilenameSegment(base)}${belegFileExtension(fileKind, receiptStorageRef)}`;
}

/**
 * Verarbeitet eine Ausgangsrechnung zu Buchungssätzen ODER einem Bucket-Eintrag.
 * Gibt entweder `{ rows }` (1 Zeile je Steuersatz) oder `{ excluded }` zurück.
 */
function processInvoice(
  inv: DatevInvoiceInput,
  skr: SkrKontenrahmen,
): { rows: DatevBookingRow[] } | { excluded: DatevExcludedItem } {
  const bucket = (reason: DatevExclusionReason): { excluded: DatevExcludedItem } => ({
    excluded: {
      source: 'invoice',
      belegnr: inv.invoiceNumber,
      dateISO: inv.dateISO,
      grossCents: inv.totalAmountCents,
      reason,
    },
  });

  // Fremdwährung → Bucket.
  if ((inv.currency ?? '').trim().toUpperCase() !== 'EUR') {
    return bucket('nonEur');
  }
  // Pflichtfelder Datum/Nummer.
  if (!inv.dateISO || !inv.invoiceNumber || inv.invoiceNumber.trim().length === 0) {
    return bucket('missingFields');
  }
  // Nullbeleg.
  if (inv.totalAmountCents === 0) {
    return bucket('zeroAmount');
  }
  if (!Array.isArray(inv.perRate) || inv.perRate.length === 0) {
    return bucket('amountMismatch');
  }

  // Brutto je Satz + Kontroll-Summe (intern, vertraut nicht blind reconstructedGrossCents).
  const bruttos = inv.perRate.map((r) => Math.trunc(r.netCents) + Math.trunc(r.vatCents));
  const sumBrutto = bruttos.reduce((a, b) => a + b, 0);

  // A9: Anker an totalAmount. Differenz >1 Cent → Bucket.
  const diff = Math.trunc(inv.totalAmountCents) - sumBrutto;
  if (Math.abs(diff) > 1) {
    return bucket('amountMismatch');
  }

  // Differenz ≤1 Cent → dem betragsgrößten Satz zuschlagen (gebuchte Summe = Beleg-Brutto).
  if (diff !== 0) {
    let maxIdx = 0;
    for (let i = 1; i < bruttos.length; i += 1) {
      if (Math.abs(bruttos[i]) > Math.abs(bruttos[maxIdx])) maxIdx = i;
    }
    bruttos[maxIdx] += diff;
  }

  // Umkehr-Trigger: Storno ODER negative Beleg-Summe.
  const isReversal = inv.isCancellation === true || inv.totalAmountCents < 0;
  const sollHaben = deriveSollHaben('revenue', isReversal);

  // M7b: documentKey nur bei vorhandener docId (sonst weglassen → Altpfad ohne Beleg).
  const documentKey =
    inv.docId && inv.docId.trim().length > 0 ? makeDocumentKey('invoices', inv.docId) : undefined;

  const rows: DatevBookingRow[] = [];
  for (let i = 0; i < inv.perRate.length; i += 1) {
    const r = inv.perRate[i];
    const mapping = mapRevenueAccount(
      r.taxRate,
      inv.isSmallBusiness,
      r.taxCategory,
      skr,
      r.taxCategorySelection ?? null,
      inv.buyerVatIdPresent === true,
    );
    if (mapping === null) {
      // Eine nicht-mappbare Position → ganze Rechnung fail-closed in den Bucket
      // (kein partieller, unabstimmbarer Beleg).
      const cat = (r.taxCategory ?? '').trim().toUpperCase();
      const reason: DatevExclusionReason =
        cat.length > 0 && cat !== 'S' ? 'taxSchemeNeedsConfirm' : 'zeroRateAmbiguous';
      return bucket(reason);
    }

    const row: DatevBookingRow = {
      umsatzCents: Math.abs(bruttos[i]),
      sollHaben,
      konto: mapping.account,
      gegenkonto: DATEV_COLLECTIVE_ACCOUNTS.debtor,
      buKey: mapping.buKey,
      belegdatumTTMM: isoToBelegdatumTTMM(inv.dateISO),
      belegfeld1: inv.invoiceNumber,
      buchungstext: buchungstext(inv.buyerName),
    };
    if (documentKey) row.documentKey = documentKey;
    rows.push(row);
  }

  return { rows };
}

/**
 * Verarbeitet eine Ausgabe zu einem Buchungssatz ODER einem Bucket-Eintrag.
 */
function processExpense(
  exp: DatevExpenseInput,
  skr: SkrKontenrahmen,
  bookedExpensesOnly: boolean,
): { rows: DatevBookingRow[] } | { excluded: DatevExcludedItem } {
  const bucket = (reason: DatevExclusionReason): { excluded: DatevExcludedItem } => ({
    excluded: {
      source: 'expense',
      belegnr: exp.invoiceNumber,
      dateISO: exp.dateISO,
      grossCents: exp.grossCents,
      reason,
    },
  });

  // Entwürfe sichtbar listen (Q-11: transparenter als stilles Ignorieren).
  if (bookedExpensesOnly && exp.status !== 'booked') {
    return bucket('draft');
  }
  // Fremdwährung → Bucket.
  if (exp.nonEurCurrency && exp.nonEurCurrency.trim().length > 0) {
    return bucket('nonEur');
  }
  // Pflichtfelder Datum/Nummer.
  if (!exp.dateISO || !exp.invoiceNumber || exp.invoiceNumber.trim().length === 0) {
    return bucket('missingFields');
  }
  // Nullbeleg.
  if (exp.grossCents === 0) {
    return bucket('zeroAmount');
  }

  const mapping = mapExpenseAccount(
    exp.taxScheme,
    exp.vatRate,
    exp.vatDeductible,
    exp.skrAccount,
    skr,
  );
  if (mapping === null) {
    return bucket('taxSchemeNeedsConfirm');
  }

  // Umkehr-Trigger: Gutschrift (381) ODER negativer Betrag.
  const isReversal = (exp.documentTypeCode ?? '').trim() === '381' || exp.grossCents < 0;
  const sollHaben = deriveSollHaben('expense', isReversal);

  // Expense-Storno (Variante B): Gegenkonto-Override statt Kreditor-Sammelkonto.
  // Verhaltensneutral für alle Nicht-Korrektur-Ausgaben (Feld undefined/null/leer).
  const gegenkontoOverride =
    typeof exp.gegenkontoOverride === 'string' && exp.gegenkontoOverride.trim().length > 0
      ? exp.gegenkontoOverride.trim()
      : null;

  // M7b: documentKey nur bei vorhandener docId (sonst weglassen → Altpfad ohne Beleg).
  const documentKey =
    exp.docId && exp.docId.trim().length > 0 ? makeDocumentKey('expenses', exp.docId) : undefined;

  const row: DatevBookingRow = {
    umsatzCents: Math.abs(Math.trunc(exp.grossCents)),
    sollHaben,
    konto: mapping.account,
    gegenkonto: gegenkontoOverride ?? DATEV_COLLECTIVE_ACCOUNTS.creditor,
    buKey: mapping.buKey,
    belegdatumTTMM: isoToBelegdatumTTMM(exp.dateISO),
    belegfeld1: exp.invoiceNumber,
    buchungstext: buchungstext(exp.supplierName),
  };
  if (documentKey) row.documentKey = documentKey;

  return { rows: [row] };
}

/**
 * Verarbeitet eine ABGERECHNETE Reise-Pauschale zu 0–2 beleglosen Buchungssätzen ODER einem
 * Bucket-Eintrag (RK-6, §3.5/§6.2). Row 1 = Verpflegung (nur wenn > 0), Row 2 = Kilometergeld
 * (nur wenn > 0) — feste Reihenfolge.
 *
 * ❌ V1-Leitplanke (§15 UStG): beide Rows tragen `buKey: null` (aus `mapReisekostenAccount`,
 * typseitig `null`) — eine beleglose Pauschale hat NIE eine abziehbare Vorsteuer. Gegenkonto
 * ist stets das Privateinlage-Konto (kein Geldabfluss). KEIN `documentKey`/`beleglink` — die
 * Buchung ist beleglos (Spalte 20 bleibt leer; kein `DatevDocumentRef`, kein Belege-ZIP-Beitrag).
 *
 * Bucket-Regeln: `draft` → `'draft'`; `cancelled` → `'travelCancelled'` (kein Umkehr-Pfad im
 * MVP, §6.3); beide Beträge 0 → `'zeroAmount'` (sichtbarer Bucket statt stiller Drop).
 */
function processTravelAllowance(
  t: DatevTravelInput,
  skr: SkrKontenrahmen,
): { rows: DatevBookingRow[] } | { excluded: DatevExcludedItem } {
  const bucket = (reason: DatevExclusionReason): { excluded: DatevExcludedItem } => ({
    excluded: {
      source: 'travel',
      belegnr: t.belegLabel,
      dateISO: t.returnDateISO,
      grossCents: Math.trunc(t.verpflegungCents) + Math.trunc(t.mileageCents),
      reason,
    },
  });

  // Lebenszyklus-Gate: nur abgerechnete (settled) Reisen werden gebucht.
  if (t.status === 'draft') {
    return bucket('draft');
  }
  if (t.status === 'cancelled') {
    return bucket('travelCancelled');
  }

  const verpflegungCents = Math.trunc(t.verpflegungCents);
  const mileageCents = Math.trunc(t.mileageCents);

  // Beide Pauschalen 0 → nichts zu buchen (sichtbarer Bucket, Muster processExpense `zeroAmount`).
  if (verpflegungCents <= 0 && mileageCents <= 0) {
    return bucket('zeroAmount');
  }

  const belegdatumTTMM = isoToBelegdatumTTMM(t.returnDateISO);
  const gegenkonto = DATEV_PRIVATEINLAGE_ACCOUNTS[skr];
  const rows: DatevBookingRow[] = [];

  if (verpflegungCents > 0) {
    const mapping = mapReisekostenAccount('verpflegung_pauschale', skr);
    rows.push({
      umsatzCents: verpflegungCents,
      sollHaben: 'S',
      konto: mapping.account,
      gegenkonto,
      buKey: mapping.buKey, // ❌ V1: immer null — nie ein Vorsteuer-Schlüssel auf einer Pauschale.
      belegdatumTTMM,
      belegfeld1: t.belegLabel,
      buchungstext: buchungstext(`${t.buchungstextBase} Verpflegung`),
    });
  }
  if (mileageCents > 0) {
    const mapping = mapReisekostenAccount('km_pauschale', skr);
    rows.push({
      umsatzCents: mileageCents,
      sollHaben: 'S',
      konto: mapping.account,
      gegenkonto,
      buKey: mapping.buKey, // ❌ V1: immer null.
      belegdatumTTMM,
      belegfeld1: t.belegLabel,
      buchungstext: buchungstext(`${t.buchungstextBase} Fahrtkosten`),
    });
  }

  return { rows };
}

// ─── buildDatevExport (Herzstück) ───────────────────────────────────────────────

/**
 * Erzeugt deterministisch den vollständigen DATEV-Buchungsstapel.
 *
 * TOTAL: wirft nie. Leere/partielle/unklare Belege landen im `excluded`-Bucket,
 * NICHT als Exception oder Falschbuchung. Periodenfilter (Soll-Versteuerung) und
 * fail-closed-Kontierung sind integriert.
 *
 * @param invoices  Ausgangsrechnungen (Erlöse), bereits Netto/USt-rekonstruiert.
 * @param expenses  Ausgaben (Aufwand/Vorsteuer).
 * @param header    Kopfzeilen-Stammdaten + Zeitraum.
 * @param options   Einbezugsregeln (default fail-closed: bookedExpensesOnly).
 * @param travel    RK-6 ADDITIV-OPTIONAL: beleglose Reise-Pauschalen. `undefined`/`[]` ⇒
 *                  Ergebnis BYTE-IDENTISCH zu heute (`csvLines`, `rows`, `excluded`,
 *                  `documentRefs`, Zähler) — Null-Delta-Garantie (E4/§3.5).
 * @returns DatevExportResult (rows + excluded + csvLines + Zähler).
 */
export function buildDatevExport(
  invoices: DatevInvoiceInput[],
  expenses: DatevExpenseInput[],
  header: DatevHeaderConfig,
  options?: DatevExportOptions,
  travel?: DatevTravelInput[],
): DatevExportResult {
  const bookedExpensesOnly = options?.bookedExpensesOnly ?? true;

  const rows: DatevBookingRow[] = [];
  const excluded: DatevExcludedItem[] = [];
  // M7b: EIN Ref je gebuchtem Beleg (mit docId), NICHT je CSV-Zeile/Steuersatz.
  const documentRefs: DatevDocumentRef[] = [];

  for (const inv of invoices ?? []) {
    if (!inPeriod(inv.dateISO, header.fromISO, header.toISO)) continue; // außer Periode → still überspringen
    const result = processInvoice(inv, header.skr);
    if ('rows' in result) {
      rows.push(...result.rows);
      if (inv.docId && inv.docId.trim().length > 0) {
        const documentKey = makeDocumentKey('invoices', inv.docId);
        // M7b-5d (P5): Datei nur erwarten, wenn eine ZUGFeRD-PDF vorliegt (`hasPdf`).
        // Reine XRechnung-XML ohne PDF ist kein lesbares Belegbild → fail-closed
        // „ohne Beleg" (kein Beleglink, kein Ref an die Function). Der Server löst die
        // Datei autoritativ auf und korrigiert real Fehlendes; fileKind bleibt 'pdf'.
        documentRefs.push({
          documentKey,
          collection: 'invoices',
          docId: inv.docId,
          fileKind: 'pdf',
          hasFileExpectation: inv.hasPdf === true,
          guid: deriveBelegGuid(documentKey),
          filenameInZip: buildBelegFilename(inv.invoiceNumber, inv.docId, 'pdf', null),
        });
      }
    } else {
      excluded.push(result.excluded);
    }
  }

  for (const exp of expenses ?? []) {
    if (!inPeriod(exp.dateISO, header.fromISO, header.toISO)) continue;
    const result = processExpense(exp, header.skr, bookedExpensesOnly);
    if ('rows' in result) {
      rows.push(...result.rows);
      if (exp.docId && exp.docId.trim().length > 0) {
        const documentKey = makeDocumentKey('expenses', exp.docId);
        const receiptStorageRef = exp.receiptStorageRef ?? null;
        const hasFileExpectation =
          receiptStorageRef != null && receiptStorageRef.trim().length > 0;
        documentRefs.push({
          documentKey,
          collection: 'expenses',
          docId: exp.docId,
          fileKind: 'receipt',
          hasFileExpectation,
          guid: deriveBelegGuid(documentKey),
          filenameInZip: buildBelegFilename(exp.invoiceNumber, exp.docId, 'receipt', receiptStorageRef),
        });
      }
    } else {
      excluded.push(result.excluded);
    }
  }

  // ── RK-6: beleglose Reise-Pauschalen (additiv, Null-Delta bei undefined/[]) ─────
  // `travel ?? []` ⇒ bei fehlendem/leerem Param läuft die Schleife NIE → rows, excluded,
  // documentRefs und (folglich) csvLines bleiben byte-identisch zum Bestandsoutput.
  // Travel-Rows erzeugen bewusst KEINEN DatevDocumentRef (beleglos, kein Belege-ZIP-Beitrag).
  for (const t of travel ?? []) {
    if (!inPeriod(t.returnDateISO, header.fromISO, header.toISO)) continue; // außer Periode → still überspringen
    const result = processTravelAllowance(t, header.skr);
    if ('rows' in result) {
      rows.push(...result.rows);
    } else {
      excluded.push(result.excluded);
    }
  }

  const csvLines: string[] = [buildExtfHeaderLine(header), buildColumnHeaderLine()];
  for (const row of rows) {
    csvLines.push(buildDataLine(row));
  }

  return {
    rows,
    excluded,
    csvLines,
    bookedCount: rows.length,
    excludedCount: excluded.length,
    documentRefs,
  };
}

/**
 * Baut die ZIP-CSV-Zeilen MIT Beleglink-Spalte 20 (M7b-5a / P1, SoT).
 *
 * Anders als `buildDatevExport().csvLines` (M7-Plain-CSV OHNE Beleglink — kein toter
 * Link in einer CSV ohne begleitendes ZIP) injiziert dieser Pfad den DATEV-Beleglink
 * per exaktem Join auf `row.documentKey` (`assignBeleglinks`) — KEIN Fuzzy-Matching
 * über Belegfeld1. `availableKeys` = `documentKey`s, für die eine Datei erwartet/
 * bestätigt ist; nur diese Rows tragen einen Beleglink (fail-closed). Ein Beleg mit
 * mehreren Steuersätzen erzeugt mehrere Rows mit demselben `documentKey` → alle
 * bekommen denselben Beleglink. Spaltenzahl unverändert (125).
 *
 * @param result       Ergebnis aus `buildDatevExport` (liefert `rows`).
 * @param header       Kopfzeilen-Stammdaten (für die EXTF-Kopfzeile).
 * @param availableKeys `documentKey`s mit real vorhandener/erwarteter Datei.
 * @returns Kopfzeile + Spaltenzeile + Beleglink-augmentierte Datenzeilen.
 */
export function buildBelegeCsvLines(
  result: DatevExportResult,
  header: DatevHeaderConfig,
  availableKeys: ReadonlySet<string>,
): string[] {
  const augmented = assignBeleglinks(result.rows, availableKeys);
  return [
    buildExtfHeaderLine(header),
    buildColumnHeaderLine(),
    ...augmented.map(buildDataLine),
  ];
}

/**
 * Baut den DATEV-Dateinamen `EXTF_Buchungsstapel_<von>_<bis>.csv`.
 */
export function buildDatevFilename(header: DatevHeaderConfig): string {
  return `EXTF_Buchungsstapel_${isoToYyyymmdd(header.fromISO)}_${isoToYyyymmdd(header.toISO)}.csv`;
}
